import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import "../graphql/invitation-data-fragment";

// GraphQL documents
const UPDATE_WORKER_EMAIL = gql(`
  mutation UpdateWorkerEmail($salonId: ID!, $workerId: ID!, $input: UpdateWorkerEmailInput!) {
    updateWorkerEmail(salonId: $salonId, workerId: $workerId, input: $input)
  }
`);

const LINK_USER_TO_WORKER = gql(`
  mutation LinkUserToWorker($salonId: ID!, $workerId: ID!, $input: LinkUserToWorkerInput!) {
    linkUserToWorker(salonId: $salonId, workerId: $workerId, input: $input)
  }
`);

const SETUP_WORKER_ACCOUNT = gql(`
  mutation SetupWorkerAccount($salonId: ID!, $workerId: ID!, $input: SetupWorkerAccountInput!) {
    setupWorkerAccount(salonId: $salonId, workerId: $workerId, input: $input) {
      action
      message
      invitationLink
    }
  }
`);

const SEARCH_USERS = gql(`
  query SearchUsers($salonId: ID!, $input: SearchUsersInput!, $franchiseId: ID) {
    searchUsers(salonId: $salonId, input: $input, franchiseId: $franchiseId) {
      users {
        id
        email
        firstName
        lastName
        isfranchiseWorker
        franchiseWorkerId
        franchiseWorkerDisplayName
      }
      total
    }
  }
`);

// Hook for updating worker email
export function useUpdateWorkerEmail() {
  return useMutation(UPDATE_WORKER_EMAIL, {
    refetchQueries: ["TeamMembers"],
    awaitRefetchQueries: true,
    errorPolicy: "none", // Throw errors to be caught in component
    onCompleted: (_data) => {},
  });
}

// Hook for linking user to worker
export function useLinkUserToWorker() {
  return useMutation(LINK_USER_TO_WORKER, {
    refetchQueries: ["TeamMembers"],
  });
}

// Hook for smart worker account setup
export function useSetupWorkerAccount() {
  return useMutation(SETUP_WORKER_ACCOUNT, {
    refetchQueries: ["TeamMembers"],
  });
}

// Hook for searching users
export function useSearchUsers(query?: string, enabled: boolean = true) {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const trimmedQuery = query?.trim() || "";

  return useQuery(SEARCH_USERS, {
    variables: {
      salonId: currentSalon?.id || "",
      input: {
        query: trimmedQuery,
        limit: 10,
      },
      franchiseId: currentSalon?.franchiseId || null,
    },
    skip: !enabled || !query || trimmedQuery.length < 2 || !currentSalon?.id,
    notifyOnNetworkStatusChange: true,
  });
}

// Hook for lazy/manual user searching
export function useLazySearchUsers() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [searchUsers, { data, loading, error }] = useLazyQuery(SEARCH_USERS);

  const searchUsersByQuery = useCallback(
    async (query: string) => {
      const trimmedQuery = query?.trim() || "";
      if (trimmedQuery.length < 2) {
        return [];
      }

      try {
        const result = await searchUsers({
          variables: {
            salonId: currentSalon?.id || "",
            input: {
              query: trimmedQuery,
              limit: 10,
            },
            franchiseId: currentSalon?.franchiseId || null,
          },
        });
        return result.data?.searchUsers?.users || [];
      } catch (err) {
        console.error("Error searching users:", err);
        return [];
      }
    },
    [searchUsers, currentSalon?.franchiseId, currentSalon?.id]
  );

  return {
    searchUsers: searchUsersByQuery,
    data,
    loading,
    error,
  };
}
